import {
  experimentAttributionFields,
  parseExperimentAttribution,
} from './experimentAttribution'

export const attributionFields = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'campaign_id',
  'adset_id',
  'ad_id',
  'placement',
  'v',
] as const

export const attributionRetentionMs = 90 * 24 * 60 * 60 * 1000

export const attributionSignalFields = attributionFields.filter(
  (field) => field !== 'v',
)

export type AttributionTouch = Partial<
  Record<
    | (typeof attributionFields)[number]
    | (typeof experimentAttributionFields)[number],
    string
  >
> & {
  capturedAt: string
  landingUrl: string
  referrer?: string
  captureMethod?: 'url' | 'referrer' | 'host' | 'legacy_google'
}

export interface LeadAttribution {
  firstTouch: AttributionTouch
  latestTouch: AttributionTouch
}

export function sanitizeLandingUrl(value: string): string | undefined {
  try {
    const url = new URL(value)
    if (
      !['http:', 'https:'].includes(url.protocol) ||
      url.username ||
      url.password
    ) {
      return undefined
    }
    const params = new URLSearchParams()
    for (const field of attributionFields) {
      const value = url.searchParams.get(field)?.trim()
      if (value && value.length <= 2048) params.set(field, value)
    }
    const query = params.toString()
    return `${url.origin}${url.pathname}${query ? `?${query}` : ''}`
  } catch {
    return undefined
  }
}

export function parseAttributionTouch(raw: unknown): AttributionTouch | null {
  if (!raw || typeof raw !== 'object') return null
  const value = raw as Record<string, unknown>
  if (
    typeof value.capturedAt !== 'string' ||
    typeof value.landingUrl !== 'string'
  ) {
    return null
  }
  const capturedAt = Date.parse(value.capturedAt)
  const landingUrl = sanitizeLandingUrl(value.landingUrl)
  if (!Number.isFinite(capturedAt) || !landingUrl) return null

  const touch: AttributionTouch = {
    capturedAt: new Date(capturedAt).toISOString(),
    landingUrl,
  }
  if (
    value.captureMethod === 'url' ||
    value.captureMethod === 'referrer' ||
    value.captureMethod === 'host' ||
    value.captureMethod === 'legacy_google'
  ) {
    touch.captureMethod = value.captureMethod
  }
  for (const field of attributionFields) {
    const fieldValue = value[field]
    if (
      typeof fieldValue === 'string' &&
      fieldValue.trim() &&
      fieldValue.length <= 2048
    ) {
      touch[field] = fieldValue.trim()
    }
  }
  const experiment = parseExperimentAttribution(value)
  if (experiment) Object.assign(touch, experiment)
  if (typeof value.referrer === 'string') {
    const referrer = sanitizeLandingUrl(value.referrer)
    if (referrer) touch.referrer = new URL(referrer).origin
  }
  return attributionSignalFields.some((field) => touch[field]) ||
    touch.referrer ||
    experiment
    ? touch
    : null
}

export function parseLeadAttribution(raw: unknown): LeadAttribution | null {
  try {
    const value = (typeof raw === 'string' ? JSON.parse(raw) : raw) as Record<
      string,
      unknown
    > | null
    if (!value || typeof value !== 'object') return null
    const firstTouch = parseAttributionTouch(value.firstTouch)
    const latestTouch = parseAttributionTouch(value.latestTouch)
    if (!firstTouch || !latestTouch) return null
    return { firstTouch, latestTouch }
  } catch {
    return null
  }
}

// Each saved lead contains a snapshot. Recover the earliest and latest touch
// across snapshots, even when the final record has no attribution.
export function mergeLeadAttribution(
  snapshots: unknown[],
  earliestAllowedAt = Number.NEGATIVE_INFINITY,
  latestAllowedAt = Number.POSITIVE_INFINITY,
  finalContext?: { experimentSnapshot: unknown },
): LeadAttribution | null {
  const touches = snapshots
    .flatMap((snapshot) => {
      const attribution = parseLeadAttribution(snapshot)
      return attribution
        ? [attribution.firstTouch, attribution.latestTouch]
        : []
    })
    .filter((touch) => {
      const time = Date.parse(touch.capturedAt)
      return time >= earliestAllowedAt && time <= latestAllowedAt
    })
    .sort((a, b) => Date.parse(a.capturedAt) - Date.parse(b.capturedAt))
  if (!touches.length) return null
  const merged = {
    firstTouch: touches[0],
    latestTouch: touches[touches.length - 1],
  }
  if (!finalContext) return merged
  // Campaign history survives, but finalization cannot restore an assignment
  // absent from the final snapshot (including after consent withdrawal).
  const current = parseLeadAttribution(finalContext.experimentSnapshot)
  const experiment =
    parseExperimentAttribution(current?.latestTouch) ??
    parseExperimentAttribution(current?.firstTouch)
  const refresh = (touch: AttributionTouch) =>
    parseAttributionTouch({
      ...Object.fromEntries(
        Object.entries(touch).filter(
          ([field]) =>
            !experimentAttributionFields.includes(
              field as (typeof experimentAttributionFields)[number],
            ),
        ),
      ),
      ...experiment,
    })
  const first = refresh(merged.firstTouch),
    latest = refresh(merged.latestTouch)
  if (!first) return latest ? { firstTouch: latest, latestTouch: latest } : null
  return { firstTouch: first, latestTouch: latest ?? first }
}
